import { ExperienceConfiguration } from '@ninetailed/experience.js-react';
import { ExperienceMapper } from '@ninetailed/experience.js-utils-contentful';
import { Reference } from '@ninetailed/experience.js-shared';
import { ContentfulModule } from 'libs/contentful/types';
import { isDraftPreviewEnabled } from 'utils/draftPreview';
import { filterValidExperiences, getContentfulClient, removeCircularReferences } from 'utils/experiences';
import { adaptEntry } from 'libs/contentful/rest/adapter/transforms';
import { buildIncludesMap } from 'libs/contentful/rest/adapter/type-guards';
import type { IncludesMap } from 'libs/contentful/rest/adapter/types';
import { processSectionModuleExperiences, SECTION_MODULE_CONFIG } from 'utils/sectionModules';

export type ExtendedExperienceConfiguration<T extends Reference> = ExperienceConfiguration<T> & {
  snowflakeName?: string;
};

export type NinetailedContentfulModule = ContentfulModule & {
  id: string;
  hidden: boolean;
};

interface NtConfigComponent {
  baseline?: { id?: string; hidden?: boolean };
  variants?: Array<{ id?: string; hidden?: boolean; value?: unknown }>;
  type?: string;
  key?: string;
  valueType?: string;
}

interface NtConfig {
  distribution?: number[];
  traffic?: number;
  sticky?: boolean;
  components?: NtConfigComponent[];
}

const isString = (value: unknown): value is string => typeof value === 'string';

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

function getEntryId(value: unknown): string | undefined {
  if (!isRecord(value)) return undefined;
  const sys = value.sys;
  return isRecord(sys) && typeof sys.id === 'string' ? sys.id : undefined;
}

/**
 * REST `getEntries({ 'sys.id': id, include: 10 })` returns the entry with all
 * linked references inline-resolved plus the includes array we use to build the
 * adapter's IncludesMap.
 */
async function fetchModuleWithIncludes(
  entryId: string,
  draftPreviewEnabled: boolean,
): Promise<{ entry: Record<string, unknown>; includesMap: IncludesMap } | null> {
  const client = getContentfulClient({ draftPreviewEnabled });
  const response = await client.getEntries({
    'sys.id': entryId,
    include: 10,
    limit: 1,
  });
  const entry = response.items[0] as unknown;
  if (!isRecord(entry)) return null;

  const includes = {
    Entry: response.includes?.Entry ?? [],
    Asset: response.includes?.Asset ?? [],
  };
  const includesMap = buildIncludesMap(includes);
  if (entry.sys && typeof (entry.sys as { id?: unknown }).id === 'string') {
    includesMap.entries.set((entry.sys as { id: string }).id, entry);
  }
  return { entry, includesMap };
}

/**
 * Returns variant Entries scoped to this module:
 * - When `nt_config.components` is present, take the component whose baseline.id
 *   matches this module's id, and pull only its variant ids in declared order.
 * - When `nt_config` (or its components) is missing, fall back to all entries in
 *   `nt_variants`.
 */
function scopeVariantsToModule(
  experienceFields: Record<string, unknown>,
  moduleEntryId: string,
): { variants: unknown[]; component: NtConfigComponent | null } {
  const ntConfig = isRecord(experienceFields.nt_config) ? (experienceFields.nt_config as NtConfig) : undefined;
  const allVariants: unknown[] = Array.isArray(experienceFields.nt_variants) ? experienceFields.nt_variants : [];

  if (!ntConfig?.components) {
    return { variants: allVariants, component: null };
  }

  const matchingComponent = ntConfig.components.find(comp => comp.baseline?.id === moduleEntryId) ?? null;
  if (!matchingComponent) {
    return { variants: [], component: null };
  }

  const componentVariantIds = (matchingComponent.variants ?? []).map(v => v?.id).filter(isString);
  const idToVariant = new Map<string, unknown>();
  for (const v of allVariants) {
    const id = getEntryId(v);
    if (id) idToVariant.set(id, v);
  }
  const orderedVariants = componentVariantIds
    .map(id => idToVariant.get(id))
    .filter((v): v is unknown => v !== undefined);

  return { variants: orderedVariants, component: matchingComponent };
}

/**
 * The Contentful ExperienceMapper validates the entry against a strict zod
 * schema (nt_experience_id required, nt_type non-empty, etc.). Pages may have
 * older nt_experience entries that miss those fields, so we fill safe defaults
 * to keep the legacy GraphQL pipeline's behavior of "use sys.id when missing".
 */
function buildScopedExperienceEntry(
  rawExperience: Record<string, unknown>,
  moduleEntryId: string,
  scopedVariants: unknown[],
): Record<string, unknown> | null {
  const fields = isRecord(rawExperience.fields) ? rawExperience.fields : null;
  if (!fields) return null;

  const sysId = getEntryId(rawExperience);
  if (!sysId) return null;

  const ntConfig = isRecord(fields.nt_config) ? (fields.nt_config as NtConfig) : undefined;
  const config: NtConfig = {
    ...(ntConfig?.distribution ? { distribution: ntConfig.distribution } : {}),
    ...(ntConfig?.traffic != null ? { traffic: ntConfig.traffic } : {}),
    ...(ntConfig?.sticky != null ? { sticky: ntConfig.sticky } : {}),
    components: [
      {
        type: 'EntryReplacement',
        baseline: { id: moduleEntryId, hidden: false },
        variants: scopedVariants
          .map(v => getEntryId(v))
          .filter(isString)
          .map(id => ({ id, hidden: false })),
      },
    ],
  };

  return {
    ...rawExperience,
    fields: {
      ...fields,
      nt_experience_id: typeof fields.nt_experience_id === 'string' ? fields.nt_experience_id : sysId,
      nt_name: typeof fields.nt_name === 'string' ? fields.nt_name : sysId,
      nt_type: typeof fields.nt_type === 'string' && fields.nt_type.length > 0 ? fields.nt_type : 'nt_experiment',
      nt_config: config,
      nt_variants: scopedVariants,
    },
  };
}

/**
 * Adapts a variant Entry to the flat shape components consume today.
 * For PageSection variants, renames the adapted `modules: { items: [...] }`
 * field to `modulesCollection` to match the legacy GraphQL contract.
 */
function adaptVariantEntry(variantEntry: unknown, includesMap: IncludesMap): Record<string, unknown> {
  const adapted = adaptEntry(variantEntry, includesMap, new WeakSet<object>(), true);
  if (!isRecord(adapted)) return {};

  if (adapted.__typename === 'PageSection') {
    const modules = adapted.modules;
    if (
      modules &&
      typeof modules === 'object' &&
      !Array.isArray(modules) &&
      'items' in (modules as Record<string, unknown>)
    ) {
      if (!('modulesCollection' in adapted)) {
        adapted.modulesCollection = modules;
      }
      delete adapted.modules;
    } else if (!('modulesCollection' in adapted)) {
      adapted.modulesCollection = { items: [] };
    }
  }

  return adapted;
}

type BaselineEntry = { sys: { id: string }; __typename?: string } & Record<string, unknown>;

/**
 * Adapts the baseline module Entry into the flat shape downstream consumers
 * expect (drops `nt_experiences` since experiences travel separately as
 * `mappedExperiences`).
 */
function adaptBaselineEntry(entry: Record<string, unknown>, includesMap: IncludesMap): BaselineEntry | null {
  const adapted = adaptEntry(entry, includesMap, new WeakSet<object>(), true);
  if (!isRecord(adapted)) return null;
  const sys = adapted.sys;
  if (!isRecord(sys) || typeof sys.id !== 'string') return null;

  delete adapted.nt_experiences;
  delete adapted.ntExperiencesCollection;

  if (adapted.__typename === 'PageSection') {
    const modules = adapted.modules;
    if (
      modules &&
      typeof modules === 'object' &&
      !Array.isArray(modules) &&
      'items' in (modules as Record<string, unknown>)
    ) {
      adapted.modulesCollection = modules;
      delete adapted.modules;
    } else if (!('modulesCollection' in adapted)) {
      adapted.modulesCollection = { items: [] };
    }
  }
  return adapted as BaselineEntry;
}

function isInlineVariableComponent(component: { type?: unknown }): boolean {
  return component?.type === 'InlineVariable';
}

function isEntryReplacementComponent(component: { type?: unknown }): boolean {
  return component?.type === 'EntryReplacement';
}

/**
 * After the Contentful mapper runs, components[].variants are still raw
 * Contentful Entry objects with an `id` field stitched on. Walk each
 * EntryReplacement component and replace its variants with the adapted flat
 * shape (plus the `id`/`hidden` annotations the legacy pipeline emitted).
 * InlineVariable components pass through unchanged.
 */
function flattenMappedComponents(
  mapped: ExtendedExperienceConfiguration<NinetailedContentfulModule>,
  moduleEntryId: string,
  scopedVariants: unknown[],
  includesMap: IncludesMap,
): void {
  if (!mapped.components?.length) return;

  const variantById = new Map<string, unknown>();
  for (const v of scopedVariants) {
    const id = getEntryId(v);
    if (id) variantById.set(id, v);
  }

  mapped.components = mapped.components.map(component => {
    const componentRecord = component as unknown as Record<string, unknown>;

    if (isInlineVariableComponent(componentRecord)) {
      return component;
    }

    if (!isEntryReplacementComponent(componentRecord)) {
      return component;
    }

    const baseline = isRecord(componentRecord.baseline) ? componentRecord.baseline : { id: moduleEntryId };
    const rawVariants = Array.isArray(componentRecord.variants) ? componentRecord.variants : [];

    const flatVariants = rawVariants
      .map(variant => {
        if (!isRecord(variant)) return null;
        const variantId = typeof variant.id === 'string' ? variant.id : getEntryId(variant);
        if (!variantId) return null;
        const sourceEntry = variantById.get(variantId) ?? variant;
        const adapted = adaptVariantEntry(sourceEntry, includesMap);
        return {
          ...adapted,
          id: variantId,
          hidden: false,
        };
      })
      .filter((v): v is { id: string; hidden: boolean } & Record<string, unknown> => v !== null);

    return {
      ...componentRecord,
      baseline: { id: baseline.id ?? moduleEntryId, hidden: false },
      variants: flatVariants,
    } as unknown as (typeof mapped.components)[number];
  });
}

const EMPTY_RESULT = {
  baselineEntry: null,
  mappedExperiences: [] as ExperienceConfiguration<ContentfulModule & { id: string }>[],
};

async function getNinetailedDataForEntry(entryId: string, draftPreviewEnabled: boolean) {
  const fetched = await fetchModuleWithIncludes(entryId, draftPreviewEnabled);
  if (!fetched) {
    console.error('No Ninetailed data found for entryId', entryId);
    return EMPTY_RESULT;
  }
  const { entry, includesMap } = fetched;

  // Re-anchor the entry so circular variants resolve cleanly through the mapper.
  const safeEntry = removeCircularReferences(entry) as Record<string, unknown>;

  const baselineEntry = adaptBaselineEntry(entry, includesMap);
  if (!baselineEntry) {
    console.error('Failed to adapt Ninetailed baseline entry', entryId);
    return EMPTY_RESULT;
  }

  const fields = isRecord(safeEntry.fields) ? safeEntry.fields : {};
  const rawExperiences: unknown[] = Array.isArray(fields.nt_experiences) ? fields.nt_experiences : [];

  const mappedExperiences = rawExperiences
    .filter(isRecord)
    .filter(exp => Boolean(getEntryId(exp)))
    .map(rawExperience => {
      const expFields = isRecord(rawExperience.fields) ? rawExperience.fields : {};
      const { variants: scopedVariants } = scopeVariantsToModule(expFields, entryId);
      const scopedExperience = buildScopedExperienceEntry(rawExperience, entryId, scopedVariants);
      if (!scopedExperience) return null;

      if (
        !ExperienceMapper.isExperienceEntry(
          scopedExperience as Parameters<typeof ExperienceMapper.isExperienceEntry>[0],
        )
      ) {
        return null;
      }

      const mapped = ExperienceMapper.mapExperience(
        scopedExperience as Parameters<typeof ExperienceMapper.mapExperience>[0],
      ) as ExtendedExperienceConfiguration<NinetailedContentfulModule>;

      if (typeof expFields.snowflakeName === 'string') {
        mapped.snowflakeName = expFields.snowflakeName;
      }

      flattenMappedComponents(mapped, entryId, scopedVariants, includesMap);

      return mapped;
    });

  return {
    baselineEntry,
    mappedExperiences: filterValidExperiences(mappedExperiences),
  };
}

/**
 * Fetches and processes Ninetailed experience data for a given entry.
 * @param entryId - The Contentful entry ID to fetch experiences for
 * @param moduleName - The name of the module type
 * @returns Object containing the baseline entry and mapped experiences
 */
export async function getNinetailedData(entryId: string, moduleName: string) {
  const draftPreviewEnabled = await isDraftPreviewEnabled();
  const result = await getNinetailedDataForEntry(entryId, draftPreviewEnabled);

  if (moduleName in SECTION_MODULE_CONFIG && moduleName !== 'PageSection') {
    const config = SECTION_MODULE_CONFIG[moduleName as keyof typeof SECTION_MODULE_CONFIG];
    const merged = await processSectionModuleExperiences(
      result.mappedExperiences as ExtendedExperienceConfiguration<NinetailedContentfulModule>[],
      moduleName as keyof typeof SECTION_MODULE_CONFIG,
      config.fetchFunction,
      config.dataProperty,
    );
    return {
      baselineEntry: result.baselineEntry,
      mappedExperiences: filterValidExperiences(
        merged as ExtendedExperienceConfiguration<NinetailedContentfulModule>[],
      ),
    };
  }

  return result;
}
